// TodayTrioCards — three permanent Home-page cards that refresh
// every day, no dismiss control:
//
//   1. ON THIS DAY — a real historical event (faith, civil-rights,
//      world-changing moment of faith) on this calendar date.
//   2. SOMETHING GOOD — an uplifting, warm story or pastoral note.
//   3. SOMEONE YOU SHOULD KNOW — a ~150-word profile of a real
//      Christian/justice figure. Feb uses the curated Black-pioneer
//      rotation; other months pick from a faith-history list.
//
// Each card is independently cached server-side per date, so users
// across the world see the same "today" content and our LLM cost
// stays near-zero after the first fetch of the day.
//
// User explicitly asked (Feb 2026):
//   "I'm going to need the daily devotional to be everyday so every
//    day something in history something good something about somebody
//    needs to be posted."
import React, { useEffect, useState } from "react";
import { Clock, HandHeart, UserCircle } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useAudio } from "../context/AudioContext";
import useCaptionTranslation from "../hooks/useCaptionTranslation";
import ReadAloudButton from "./ReadAloudButton";
import PreviousDaysDrawer, { prettyDate } from "./PreviousDaysDrawer";

// User's LOCAL calendar date as YYYY-MM-DD. Used to override the
// server's UTC `date.today()` so a viewer in US Eastern at 6 PM on
// May 21 sees that day's card (not May 22's, because UTC has already
// rolled over). User reported: "It's may 22nd the daily card for on
// this day in black history needs to be adjusted — it's a day early."
//
// We also expose this as a hook that refreshes when the tab regains
// focus, so leaving the tab open across midnight rolls the cards.
function getLocalISODate() {
  // Anchored to America/Los_Angeles (Pacific) — user runs the app
  // from California and wants every viewer to see the same day's
  // content based on her local clock. Without this, a viewer in
  // Europe rolling past midnight UTC would see "tomorrow's" card 7-9
  // hours before users on the US west coast.
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    // en-CA gives "YYYY-MM-DD" directly.
    if (/^\d{4}-\d{2}-\d{2}$/.test(parts)) return parts;
  } catch (_) {}
  // Fallback to browser-local date if Intl is unavailable.
  try {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch (_) {
    return null;
  }
}

function useLocalDateISO() {
  const [date, setDate] = useState(() => getLocalISODate());
  useEffect(() => {
    const refresh = () => {
      const next = getLocalISODate();
      setDate((prev) => (prev === next ? prev : next));
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    // Also check every 5 min in case the user stays on the tab.
    const id = setInterval(refresh, 5 * 60 * 1000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      clearInterval(id);
    };
  }, []);
  return date;
}

function CardShell({ children, accent = "amber", testid, loading = false, empty = false }) {
  // Accent → visual identity per card so the three sit side-by-side
  // without looking like one beige wall.
  const PALETTES = {
    amber:      { bg: "from-amber-100 via-amber-200/70 to-amber-300/60", text: "text-ink",     chip: "bg-ink/10 text-ink",       border: "border-amber-300/60" },
    cream:      { bg: "from-cream via-cream-100 to-amber-100/60",        text: "text-ink",     chip: "bg-forest/10 text-forest", border: "border-forest/15" },
    terracotta: { bg: "from-terracotta via-terracotta-600 to-rose-700",  text: "text-cream",   chip: "bg-cream/15 text-cream",   border: "border-cream/15" },
  };
  const p = PALETTES[accent] || PALETTES.amber;
  if (loading) {
    return (
      <div data-testid={`${testid}-loading`} className={`rounded-3xl bg-gradient-to-br ${p.bg} ${p.text} border ${p.border} p-6 shadow-lg relative overflow-hidden`}>
        <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-cream/15 blur-3xl pointer-events-none" />
        <div className="relative">
          {/* Pill skeleton */}
          <div className="w-32 h-5 rounded-full bg-current/15 animate-pulse" />
          {/* Title skeleton */}
          <div className="mt-4 h-7 w-5/6 rounded-md bg-current/15 animate-pulse" />
          <div className="mt-2 h-7 w-2/3 rounded-md bg-current/15 animate-pulse" />
          {/* Body skeleton lines */}
          <div className="mt-4 space-y-2">
            <div className="h-3 rounded bg-current/10 animate-pulse" />
            <div className="h-3 w-11/12 rounded bg-current/10 animate-pulse" />
            <div className="h-3 w-10/12 rounded bg-current/10 animate-pulse" />
            <div className="h-3 w-9/12 rounded bg-current/10 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }
  if (empty) return null;
  return (
    <div data-testid={testid} className={`rounded-3xl bg-gradient-to-br ${p.bg} ${p.text} border ${p.border} p-6 md:p-7 shadow-lg relative overflow-hidden`}>
      <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-cream/15 blur-3xl pointer-events-none" />
      <div className="relative">{children({ chipClass: p.chip })}</div>
    </div>
  );
}

function OnThisDayCard() {
  const { t } = useTranslation();
  const { language } = useAudio();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const localDate = useLocalDateISO();
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/today/on-this-day", { params: localDate ? { date_iso: localDate } : {} })
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [localDate]);
  // Translate dynamic strings into the active UI language. Falls back
  // to the original English while the async translation is in flight
  // (cached per-language so re-renders are free).
  const trTitle = useCaptionTranslation(d?.title || "", language);
  const trBody = useCaptionTranslation(d?.body || "", language);
  return (
    <CardShell accent="amber" testid="today-history-card" loading={loading} empty={!d}>
      {({ chipClass }) => (
        <>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.22em] uppercase font-bold ${chipClass}`}>
            <Clock size={11} weight="fill" /> {t("today.onThisDay")}{d?.year ? ` · ${d.year}` : ""}
          </span>
          <h3 className="font-heading text-xl md:text-2xl mt-3 leading-tight" data-testid="today-history-title">{trTitle || d?.title}</h3>
          <p className="mt-2 text-sm md:text-[0.95rem] leading-relaxed opacity-85" data-testid="today-history-body">{trBody || d?.body}</p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <ReadAloudButton
              text={`${trTitle || d?.title || ""}. ${trBody || d?.body || ""}`}
              testid="today-history-read-aloud"
              nowPlayingLabel={d?.title ? `On this day · ${d.title}` : "On this day"}
              variant="dark"
              prefetch
            />
            <PreviousDaysDrawer
              kind="on-this-day-bh"
              label={t("today.previousDays")}
              pillClass={chipClass}
              readAloudVariant="dark"
              render={(it) => ({
                headline: it.title + (it.year ? ` · ${it.year}` : ""),
                body: it.body,
                dateText: prettyDate(it.date),
              })}
            />
          </div>
        </>
      )}
    </CardShell>
  );
}

function SomethingGoodCard() {
  const { t } = useTranslation();
  const { language } = useAudio();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const localDate = useLocalDateISO();
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/today/good-news", { params: localDate ? { date_iso: localDate } : {} })
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [localDate]);
  const trHeadline = useCaptionTranslation(d?.headline || "", language);
  const trBody = useCaptionTranslation(d?.body || "", language);
  return (
    <CardShell accent="cream" testid="today-good-card" loading={loading} empty={!d}>
      {({ chipClass }) => (
        <>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.22em] uppercase font-bold ${chipClass}`}>
            <HandHeart size={11} weight="fill" /> {t("today.somethingGood")}
          </span>
          <h3 className="font-heading text-xl md:text-2xl mt-3 leading-tight" data-testid="today-good-title">{trHeadline || d?.headline}</h3>
          <p className="mt-2 text-sm md:text-[0.95rem] leading-relaxed opacity-85" data-testid="today-good-body">{trBody || d?.body}</p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <ReadAloudButton
              text={`${trHeadline || d?.headline || ""}. ${trBody || d?.body || ""}`}
              testid="today-good-read-aloud"
              nowPlayingLabel={d?.headline ? `Something good · ${d.headline}` : "Something good"}
              variant="dark"
              prefetch
            />
            <PreviousDaysDrawer
              kind="good-news"
              label={t("today.previousDays")}
              pillClass={chipClass}
              readAloudVariant="dark"
              render={(it) => ({
                headline: it.headline,
                body: it.body,
                dateText: prettyDate(it.date),
              })}
            />
          </div>
        </>
      )}
    </CardShell>
  );
}

function PersonOfDayCard() {
  const { t } = useTranslation();
  const { language } = useAudio();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const localDate = useLocalDateISO();
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/today/person-of-day", { params: localDate ? { date_iso: localDate } : {} })
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [localDate]);
  // Name stays English (proper noun). Blurb + story translate.
  const trBlurb = useCaptionTranslation(d?.blurb || "", language);
  const trStory = useCaptionTranslation(d?.story || "", language);
  return (
    <CardShell accent="terracotta" testid="today-person-card" loading={loading} empty={!d}>
      {({ chipClass }) => (
        <>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.22em] uppercase font-bold ${chipClass}`}>
            <UserCircle size={11} weight="fill" /> {t("today.someoneToKnow")}
          </span>
          <h3 className="font-heading text-xl md:text-2xl mt-3 leading-tight" data-testid="today-person-name">{d?.name}</h3>
          {d?.blurb && (
            <p className="mt-1.5 text-[11px] tracking-wider uppercase font-semibold opacity-80" data-testid="today-person-blurb">{trBlurb || d.blurb}</p>
          )}
          <p className="mt-2 text-sm md:text-[0.95rem] leading-relaxed opacity-90" data-testid="today-person-story">{trStory || d?.story}</p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <ReadAloudButton
              text={`${d?.name || ""}. ${trStory || d?.story || ""}`}
              testid="today-person-read-aloud"
              nowPlayingLabel={d?.name ? `Someone you should know · ${d.name}` : "Someone you should know"}
              variant="light"
              prefetch
            />
            <PreviousDaysDrawer
              kind="person-of-day"
              label={t("today.previousDays")}
              pillClass={chipClass}
              readAloudVariant="light"
              render={(it) => ({
                headline: it.name,
                body: it.story,
                dateText: prettyDate(it.date),
              })}
            />
          </div>
        </>
      )}
    </CardShell>
  );
}

export default function TodayTrioCards() {
  return (
    <section
      data-testid="today-trio-cards"
      className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5"
    >
      <OnThisDayCard />
      <SomethingGoodCard />
      <PersonOfDayCard />
    </section>
  );
}
